/* This example requires Tailwind CSS v2.0+ */
import { useState, useEffect, useMemo } from 'react'
import { Switch } from '@headlessui/react'
import { isObject, isUndefined } from 'lodash'
import { sanitizeText } from '../../helpers/utils'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function Toggle(props) {
    let {
        value,
        onChange,
        setValue,
        getValues,
        watch,
        item,
        label,
        description,
        shouldReset,
    } = props

    label = label || item?.label || item?.name
    description = description || item?.description

    const isBinarySelectToggle =
        item?.type === 'select' &&
        Array.isArray(item?.options) &&
        item.options.length === 2 &&
        item.options[0] === 'No'

    const getBooleanValue = (incomingValue) => {
        if (typeof incomingValue === 'boolean') return incomingValue
        if (typeof incomingValue === 'string') return incomingValue !== 'No'
        return false
    }

    const getStoredValue = (incomingValue) => {
        if (!isBinarySelectToggle) return incomingValue
        return incomingValue ? item.options[1] : item.options[0]
    }

    const watchedValue = item?.name && watch ? watch(item.name) : undefined
    const formValue = !isUndefined(watchedValue)
        ? watchedValue
        : item?.name
        ? getValues?.(item.name)
        : undefined
    const externalValue = !isUndefined(formValue)
        ? formValue
        : isBinarySelectToggle
        ? undefined
        : value

    value = useMemo(() => {
        if (isBinarySelectToggle) {
            return getBooleanValue(
                isUndefined(externalValue) ? item?.value : externalValue,
            )
        }

        if (!isUndefined(externalValue)) return getBooleanValue(externalValue)
        if (!isUndefined(item?.value)) return getBooleanValue(item.value)

        return false
    }, [externalValue, isBinarySelectToggle, item?.value])

    const toggleVariant =
        item?.toggleVariant || (isBinarySelectToggle ? 'upgrade' : 'default')

    const [enabled, setEnabled] = useState(value)
    const [helperText, setHelperText] = useState('')

    const handleToggleChange = (value) => {
        setEnabled(value)

        if (setValue && item?.name) {
            setValue(item.name, getStoredValue(value))
        }

        if (onChange) onChange(value)
    }

    useEffect(() => {
        shouldReset && setEnabled(value)
    }, [shouldReset, value])

    useEffect(() => {
        setEnabled(value)
    }, [value])

    useEffect(() => {
        if (!isBinarySelectToggle && setValue && item?.name && getValues) {
            const currentValue = getValues(item.name)

            if (isUndefined(currentValue) && !isUndefined(item?.value)) {
                setValue(item.name, item.value)
            }
        }
    }, [getValues, isBinarySelectToggle, item?.name, item?.value, setValue])

    useEffect(() => {
        if (isObject(item?.helperText)) {
            setHelperText(item.helperText[enabled || false])
        }
    }, [enabled, item?.helperText])

    const isDisabled = Boolean(item?.disabled)
    const rawDescription = helperText || description || ''
    const descriptionStartsWithPrice = /^\$[\d.]+\s+/i.test(rawDescription)
    const fallbackPriceLabel = descriptionStartsWithPrice
        ? `+${rawDescription.match(/^\$[\d.]+/i)?.[0] || ''}`.replace('++', '+')
        : ''
    const priceLabel = item?.priceLabel || fallbackPriceLabel
    const detailText = descriptionStartsWithPrice
        ? rawDescription.replace(/^\$[\d.]+\s+additional fee\.\s*/i, '')
        : rawDescription
    const tooltipId = item?.name
        ? `${sanitizeText(item.name)}-tooltip`
        : undefined

    const renderInfoTooltip = () => {
        if (!detailText) return null

        return (
            <button
                type="button"
                tabIndex={0}
                aria-label={`More information about ${label}`}
                aria-describedby={tooltipId || undefined}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                className="group/info relative inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] border-gray-400 bg-transparent p-0 font-serif text-[10px] font-bold italic leading-none text-gray-400 hover:border-[#ED5C2F] hover:text-[#ED5C2F] focus:border-[#ED5C2F] focus:text-[#ED5C2F] focus:outline-none">
                i
                <span
                    id={tooltipId || undefined}
                    role="tooltip"
                    className="pointer-events-none absolute bottom-[150%] right-[-3px] z-[60] w-[230px] rounded-lg bg-[#1A1D23] px-3 py-2 text-left font-sans text-[11.5px] font-medium not-italic leading-[1.45] text-white opacity-0 shadow-[0_12px_34px_rgba(26,29,35,0.18)] transition-opacity duration-150 group-hover/info:opacity-100 group-focus/info:opacity-100">
                    {detailText}
                    <span className="absolute right-[9px] top-full h-0 w-0 border-[5px] border-transparent border-t-[#1A1D23]" />
                </span>
            </button>
        )
    }

    const switchClassName = classNames(
        isDisabled ? 'cursor-not-allowed opacity-70' : '',
        enabled
            ? 'bg-[linear-gradient(90deg,#ED5C2F,#F28230)]'
            : 'bg-[#D9D2C6]',
        'relative inline-flex h-[26px] w-[46px] flex-shrink-0 items-center rounded-full border-none p-[3px] transition-colors duration-200',
    )

    const switchKnobClassName = classNames(
        enabled ? 'translate-x-5' : 'translate-x-0',
        'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transform transition duration-200 ease-in-out',
    )

    if (toggleVariant === 'compact') {
        return (
            <Switch.Group
                as="div"
                className="flex items-center gap-3 border-t border-[#ECE6DC] py-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Switch.Label
                            as="span"
                            className="text-[13px] font-semibold leading-5 text-olm-black"
                            passive>
                            {label}
                        </Switch.Label>
                        {renderInfoTooltip()}
                        {priceLabel && (
                            <span className="text-xs font-bold text-olm whitespace-nowrap">
                                {priceLabel}
                            </span>
                        )}
                    </div>
                </div>
                <Switch
                    data-switch-id={`${sanitizeText(item?.name)}`}
                    checked={enabled}
                    onChange={handleToggleChange}
                    disabled={isDisabled}
                    className={switchClassName}>
                    <span aria-hidden="true" className={switchKnobClassName} />
                </Switch>
            </Switch.Group>
        )
    }

    if (toggleVariant === 'upgrade') {
        return (
            <Switch.Group
                as="div"
                className={classNames(
                    isDisabled ? 'opacity-70' : '',
                    enabled
                        ? 'border-[#F6D9C2] shadow-[0_0_0_3px_rgba(242,130,48,0.08)]'
                        : 'border-[#E1DACE]',
                    'mb-2 rounded-xl border bg-white px-4 py-3 transition-all duration-150',
                )}>
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Switch.Label
                                as="span"
                                className="text-sm font-semibold leading-5 text-olm-black"
                                passive>
                                {label}
                            </Switch.Label>
                            {priceLabel && (
                                <span className="inline-flex items-center rounded-full bg-[#FDF4EC] px-2.5 py-1 text-[11.5px] font-bold leading-none text-[#ED5C2F] whitespace-nowrap">
                                    {priceLabel}
                                </span>
                            )}
                            {renderInfoTooltip()}
                        </div>
                    </div>
                    <Switch
                        data-switch-id={`${sanitizeText(item?.name)}`}
                        checked={enabled}
                        onChange={handleToggleChange}
                        disabled={isDisabled}
                        className={switchClassName}>
                        <span
                            aria-hidden="true"
                            className={switchKnobClassName}
                        />
                    </Switch>
                </div>
            </Switch.Group>
        )
    }

    return (
        <Switch.Group
            as="div"
            className="flex items-center justify-between gap-4 py-4">
            {label && (
                <span className="flex-grow flex flex-col">
                    <Switch.Label
                        as="span"
                        className="text-lg leading-6 font-medium text-olm-black"
                        passive>
                        {label}
                    </Switch.Label>
                    {(helperText || description) && (
                        <Switch.Description
                            as="span"
                            className="text-sm font-normal text-gray-500">
                            {helperText || description}
                        </Switch.Description>
                    )}
                </span>
            )}
            <Switch
                data-switch-id={`${sanitizeText(item?.name)}`}
                checked={enabled}
                onChange={handleToggleChange}
                disabled={isDisabled}
                className={switchClassName}>
                <span aria-hidden="true" className={switchKnobClassName} />
            </Switch>
        </Switch.Group>
    )
}
